import { Badge, Button } from '@monksflow/monks-ui';
import { EyeIcon, MessageSquareIcon, SirenIcon } from 'lucide-react';

import { CATEGORY_COLORS, type CategoryId } from '@/shared/constants/category-colors';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import quoteIcon from '@/shared/assets/images/quote.svg';
import { TruncatedText } from '@/shared/ui/TruncatedText/TruncatedText';
import { SparkSkeletonCard } from '../SparkSkeletonCard';
import type { SparkCardProps } from './SparkCard.types';
import { SENTIMENT_BADGE_VARIANT, SENTIMENT_LABEL_KEY } from './SparkCard.constants';
import { useTranslation } from 'react-i18next';

/**
 * Sparks surface social-media opportunities, which the tools hub files under
 * `plan` — so the badge takes that category's colours rather than its own.
 */
const OPPORTUNITY_CATEGORY = 'plan' satisfies CategoryId;

export const SparkCard = ({
  spark,
  onViewOpportunity,
  onStartChat,
  noBorder = false,
}: SparkCardProps) => {
  const { t } = useTranslation('audiences');

  const { downloadUrl: imageUrl, isLoading } = useGetDownloadUrl(spark.persona.assetId);

  if (isLoading) return <SparkSkeletonCard />;

  const sentimentVariant = spark.sentiment ? SENTIMENT_BADGE_VARIANT[spark.sentiment] : undefined;

  // Consumers outside the homepage build a spark with no opportunity on it, and
  // one of them renders into a client-facing PDF — so the footer appears only
  // when it has something to name.
  const opportunity = (spark.opportunityTitle ?? spark.source).trim();

  return (
    <div
      className={`relative flex w-full flex-col gap-lg rounded-lg border bg-transparent p-xl transition-[border-color] duration-fast ease-out ${noBorder ? 'border-border-light' : 'border-border-light hover:border-border-strong'}`}
    >
      {/* Pinned to the corner rather than placed in the column, so the portrait
          keeps the full content width beneath it. */}
      <div className="absolute top-xl right-xl flex items-center gap-xs">
        {onViewOpportunity && (
          <Button variant="ghost" size="sm" onClick={() => onViewOpportunity(spark)}>
            <EyeIcon className="size-4" />
            {t('personaSpark.sparkCard.viewOpportunity')}
          </Button>
        )}
        {onStartChat && (
          <Button variant="ghost" size="sm" onClick={() => onStartChat(spark)}>
            <MessageSquareIcon className="size-4" />
            {t('personaSpark.sparkCard.startChat')}
          </Button>
        )}
      </div>

      <div className="size-22 shrink-0 overflow-hidden rounded-full bg-neutral-100 dark:bg-surface-raised">
        {imageUrl && (
          <img src={imageUrl} alt={spark.persona.name} className="size-full object-cover" />
        )}
      </div>

      <div className="relative flex flex-col gap-xs">
        <img
          src={quoteIcon}
          alt=""
          className="pointer-events-none absolute top-1 left-0 h-[14px] w-[15px] dark:invert"
        />

        <TruncatedText
          value={spark.spark}
          lines={2}
          className="pl-xl text-lg leading-6 font-medium tracking-[-0.02em] text-foreground"
        />

        <div className="flex flex-wrap items-center gap-xs pl-xl">
          <span className="text-xs leading-5 text-foreground-subtle">{spark.persona.role}</span>
          {sentimentVariant && (
            <Badge variant={sentimentVariant} roundness="full" size="sm">
              {spark.sentiment && t(SENTIMENT_LABEL_KEY[spark.sentiment])}
            </Badge>
          )}
        </div>
      </div>

      {opportunity && (
        <div className="flex items-center gap-sm border-t border-border-light pt-md">
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-sm"
            style={{ backgroundColor: CATEGORY_COLORS[OPPORTUNITY_CATEGORY].background }}
          >
            <SirenIcon className="size-4" color={CATEGORY_COLORS[OPPORTUNITY_CATEGORY].text} />
          </span>

          <div className="flex min-w-0 flex-col gap-3xs">
            <span className="text-[11px] leading-[14px] font-medium tracking-[0.015em] text-muted-foreground uppercase">
              {t('personaSpark.sparkCard.opportunity')}
            </span>
            <TruncatedText
              value={opportunity}
              className="text-sm leading-5 font-medium text-foreground"
            />
          </div>
        </div>
      )}
    </div>
  );
};
